import { useUserState } from 'modules/user/UserContext';
import {
  DexOrderStatus,
  DexTradeStatus,
  GetDexOpenPositionsParams,
  isPositionExecuted,
  LimitOrderSide,
  OpenPositionListItem,
  OpenPositionListItem as ItemData,
  SnipePositionStatus,
} from 'api/types/httpsTypes/dex-trade';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  dexTradeKeys,
  DexTradeService,
} from 'api/services/httpServices/DexTradeService';
import { useInView } from 'react-intersection-observer';
import Button from 'modules/shared-components/button/button';
import { Link, useHistory } from 'react-router-dom';
import { LoadingComponent } from './LoadingComponent';
import { twMerge } from 'tailwind-merge';
import Big from 'big.js';
import { ActionsMenu, SnipeStatus } from '../trade-monitor/columns';
import { TokenNameWithChain } from './SharedComponentsPanel';
import { useToggle } from 'modules/utils/useToggle';
import {
  ChartBarSquareIcon,
  ClockIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import {
  DurationCounter,
  HoldingTimeCounter,
} from '../research/components/TelegramCalls/columns';
import { TokenIconWithChain } from 'modules/shared-components/asset/token-icon';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { chainAsset } from 'api/types/httpsTypes/d-wallets';
import Tooltip from 'modules/shared-components/tooltip';
import { formatHoldingTime } from 'modules/dTrade/utils';
import { FullScreenModal } from '../components/modal/FullScreenModal';
import { PositionDetails } from '../trade-monitor/components/Details/PositionDetails';
import { LoaderDex } from '../components/alerts/Loader';
import {
  orderStatesStatuses,
  positionStatesStatuses,
} from '../trade-monitor/usePageState';
import { Tabs } from '../components/layout/SIDEBAR_CONST';
import { NotificationDex } from '../components/alerts/notification';
import Checkbox from 'modules/shared-components/input/Checkbox';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClosePosition } from '../trade-monitor/ClosePosition';
import { PerformanceStyle } from '../components/alerts/performanceStyle';
import { useNetUnrealizedLivePnl } from '../trade-monitor/netUnrealizedLivePnl';
import { usePositionUpdate } from 'api/hooks/usePositionUpdate';
import { updatePositionsList } from '../trade-monitor/components/updatePositionWebsocket';
import { useSelectedChain } from '../components/chains/getChains';
import { useChainInfo } from 'api/hooks/useChainInfo';
import { Pagination } from 'api/types/httpsTypes';
import { useWebsocket } from 'api/hooks/useWebsocket';

interface PnlProps {
  position: OpenPositionListItem;
  chainAssetPrice: string | null | undefined;
}

const PnlIcons = ({ position }: PnlProps) => (
  <>
    {position.volatilityBotId && (
      <Tooltip text="Volatility bot snipe">
        <div className="pb-0.5">
          <ChartBarSquareIcon className="w-3 h-3 " />
        </div>
      </Tooltip>
    )}
    {position.copyTradingBotId && (
      <Tooltip text="Wallet copy-trading">
        <div className="pb-0.5">
          <CreditCardIcon className="w-3 h-3 " />
        </div>
      </Tooltip>
    )}
    {position.telegramSnipingCallEvent && (
      <Tooltip text="Telegram copy-trading">
        <div className="pb-0.5">
          <PhoneIcon className="w-3 h-3 " />
        </div>
      </Tooltip>
    )}
  </>
);

const UnrealizedPnlComponent = ({ position, chainAssetPrice }: PnlProps) => {
  const unrealizedPnl = useNetUnrealizedLivePnl(position, chainAssetPrice);

  const pnlAmountInEth = Big(
    unrealizedPnl?.absolute ??
      position.netUnrealisedProfitAndLoss?.pnlAmountInEth ??
      0
  );

  const pnlPct = Big(
    unrealizedPnl?.relative ?? position.netUnrealisedProfitAndLoss?.pnlPct ?? 0
  ).mul(100);

  return (
    <div className="flex flex-column xxs:space-y-0.5 xxs:text-right">
      <div className="flex space-x-1 items-center justify-end">
        <PnlIcons position={position} chainAssetPrice={chainAssetPrice} />
        <PerformanceStyle
          pnl={pnlPct.toString()}
          asset="%"
          className="xxs:text-sm"
        />
      </div>
      <div className="xxs:text-xs">
        <PerformanceStyle
          pnl={pnlAmountInEth.toString()}
          asset={chainAsset(position.chain)}
        />
      </div>
    </div>
  );
};

const PnlComponent = ({ position, chainAssetPrice }: PnlProps) => {
  const isPositionClosed = [
    DexTradeStatus.closed,
    DexTradeStatus.closedByUser,
    DexTradeStatus.closedInProgress,
  ].includes(position.status);

  if (!isPositionClosed)
    return (
      <UnrealizedPnlComponent
        position={position}
        chainAssetPrice={chainAssetPrice}
      />
    );

  const realisedPnlAmount = Big(
    position.realisedProfitAndLoss?.pnlAmountInEth ?? 0
  );
  const realisedPnlPct = Big(position.realisedProfitAndLoss?.pnlPct ?? 0).mul(
    100
  );

  return (
    <div className="flex flex-column xxs:space-y-0.5 xxs:text-right">
      <div className="flex space-x-1 items-center justify-end">
        <PnlIcons position={position} chainAssetPrice={chainAssetPrice} />
        <PerformanceStyle
          pnl={realisedPnlPct.toString()}
          asset="%"
          className="xxs:text-sm"
        />
      </div>
      <div className="xxs:text-xs">
        <PerformanceStyle
          pnl={realisedPnlAmount.toString()}
          asset={chainAsset(position.chain)}
        />
      </div>
    </div>
  );
};

const LIMIT = 10;

export function SnipesPanel({ params }: { params: GetDexOpenPositionsParams }) {
  const { user } = useUserState();
  const prevParamsRef = useRef(params);
  const isDesktop = useMediaQuery(screenGte.large);
  const [selectedTokens, setSelectedTokens] = useState<string[]>([]); // Explicit type
  const [openCloseAllModal, { open, close }] = useToggle();
  const [items, setItems] = useState<OpenPositionListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: LIMIT,
    offset: 0,
    total: 0,
  });

  const selectedChain = useSelectedChain();
  const updatedPosition = usePositionUpdate(selectedChain);

  useEffect(() => {
    setItems([]);
    setPagination({
      limit: LIMIT,
      offset: 0,
      total: 0,
    });
  }, [selectedChain]);

  const {
    error,
    isInitialLoading: isLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = { limit: LIMIT, offset: 0 } }) => {
      return DexTradeService.getPositions({
        ...params,
        limit: pageParam.limit,
        offset: pageParam.offset,
      });
    },
    queryKey: dexTradeKeys.getPositions(params),

    getNextPageParam: () => ({
      offset: pagination.offset,
      limit: LIMIT,
    }),

    enabled: !!user,
    cacheTime: 0,
    staleTime: 0,

    onSuccess: (newData) => {
      const isNewFilter =
        prevParamsRef.current.tokenAddress !== params.tokenAddress ||
        prevParamsRef.current.botId !== params.botId;

      const pages = newData.pages;
      const newPage = pages[pages.length - 1];
      const newPositions = newPage.data;

      if (isNewFilter) {
        // Reset state on new filter
        setItems(newPositions);
        setPagination(() => ({
          total: newPage.pagination.total,
          limit: newPage.pagination.limit,
          offset: newPage.pagination.offset,
        }));
      } else {
        // Append deduplicated positions
        setItems((prevItems) => {
          const seen = new Set(prevItems.map((i) => i.id));
          const filtered = newPositions.filter((i) => !seen.has(i.id));
          return [...prevItems, ...filtered];
        });

        setPagination((prev) => ({
          total: newPage.pagination.total,
          limit: newPage.pagination.limit,
          offset: prev.offset + newPositions.length,
        }));
      }

      prevParamsRef.current.tokenAddress = params.tokenAddress;
      prevParamsRef.current.botId = params.botId;
    },
  });

  const isActivePosition = params.status === positionStatesStatuses.active;

  useEffect(() => {
    if (!updatedPosition) return;

    setItems((prevItems) => {
      const { updatedItems, wasAdded, wasRemoved } = updatePositionsList(
        prevItems,
        updatedPosition,
        isActivePosition,
        params.tokenAddress,
        params.botId
      );

      if (wasAdded || wasRemoved) {
        setPagination((prev) => {
          const newTotal = prev.total + (wasAdded ? 1 : wasRemoved ? -1 : 0);
          return {
            ...prev,
            total: newTotal,
            offset: wasRemoved ? Math.max(prev.offset - 1, 0) : prev.offset,
          };
        });
      }

      return updatedItems;
    });
  }, [updatedPosition]);

  const totalLoaded = items?.length ?? 0;
  const hasMore = totalLoaded < pagination.total;

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasMore) {
        void fetchNextPage();
      }
    },
  });

  if (error) {
    return (
      <div className="divide-x-0 divide-solid dark:divide-black-700 xxs:divide-white-700 xxs:h-[600px] lg:h-auto divide-y overflow-y-auto no-scrollbar overflow-y-scroll">
        <div className="py-1 text-sm absolute bottom-50 flex items-center justify-center w-full flex-col space-y-1">
          {error && (
            <NotificationDex type="error" errorMessage={error}>
              An error occurred
            </NotificationDex>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (items.length === 0) {
    return (
      <div className="divide-x-0 divide-solid xxs:divide-dex-black-700 xxs:h-[600px] lg:h-auto divide-y overflow-y-auto no-scrollbar overflow-y-scroll">
        <div className="py-1 text-sm absolute bottom-50 flex items-center justify-center w-full flex-col space-y-1">
          <div className="xxs:text-dex-white-secondary">No trades found</div>

          {!isDesktop && (
            <>
              <Button
                as={Link}
                variant="primary"
                to="/dex/research?searchDefaultOpen=true"
              >
                Snipe
              </Button>
              {params.status?.includes(DexTradeStatus.new) && (
                <Button as={Link} to={`/dex/trade-monitoring/${Tabs.History}`}>
                  Check history
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedTokens && selectedTokens.length >= 1 && (
        <div className="xxs:px-1">
          <Button
            onClick={open}
            className="xxs:px-2 xxs:py-2 w-full xxs:text-base"
            variant="errorDex"
          >
            SELL ALL ({selectedTokens.length})
          </Button>
          {openCloseAllModal && items && items.length >= 1 && (
            <ClosePosition
              setSelectedTokens={setSelectedTokens}
              position={items.filter(
                (trade: OpenPositionListItem) =>
                  selectedTokens &&
                  selectedTokens.length >= 1 &&
                  selectedTokens.includes(trade.id)
              )}
              onCancel={close}
            />
          )}
        </div>
      )}
      <div className="divide-x-0 divide-solid xxs:divide-dex-black-700 divide-y overflow-y-auto no-scrollbar overflow-y-scroll">
        {items.map((position) => {
          return (
            <SnipesList
              selectedTokens={selectedTokens}
              setSelectedTokens={setSelectedTokens}
              isHistory={params.status === positionStatesStatuses.history}
              key={position.id}
              position={position}
            />
          );
        })}
        {hasMore && (
          <>
            <div className="flex justify-center xxs:py-2">
              <LoaderDex className="mx-auto" />
            </div>
            <div ref={ref} style={{ height: 1 }} />
          </>
        )}
      </div>
    </>
  );
}
// Main exported component that decides which implementation to use
export function SnipesList(props: {
  selectedTokens?: string[];
  setSelectedTokens?: React.Dispatch<React.SetStateAction<string[]>>;
  position: ItemData;
  isMerge?: boolean;
  className?: string;
  isHistory?: boolean;
}) {
  // Render the appropriate version based on isHistory
  return props.isHistory ? (
    <SnipesListWithoutSubscription {...props} />
  ) : (
    <SnipesListWithSubscription {...props} />
  );
}

// Version with websocket subscription for active positions
function SnipesListWithSubscription({
  position,
  isMerge,
  className,
  isHistory,
  selectedTokens,
  setSelectedTokens,
}: {
  selectedTokens?: string[];
  setSelectedTokens?: React.Dispatch<React.SetStateAction<string[]>>;
  position: ItemData;
  isMerge?: boolean;
  className?: string;
  isHistory?: boolean;
}) {
  // Include the websocket subscription for active positions
  const priceSub = useMemo(
    () => ({
      chain: position.chain,
      address: position.tokenAddress,
    }),
    [position.chain, position.tokenAddress]
  );

  useWebsocket('token_price', priceSub);

  // Pass to the base implementation
  return (
    <SnipesListBase
      position={position}
      isMerge={isMerge}
      className={className}
      isHistory={isHistory}
      selectedTokens={selectedTokens}
      setSelectedTokens={setSelectedTokens}
    />
  );
}

// Version without websocket subscription for history
function SnipesListWithoutSubscription(props: {
  selectedTokens?: string[];
  setSelectedTokens?: React.Dispatch<React.SetStateAction<string[]>>;
  position: ItemData;
  isMerge?: boolean;
  className?: string;
  isHistory?: boolean;
}) {
  // No websocket subscription here
  return <SnipesListBase {...props} />;
}

// Base implementation with all the shared logic
function SnipesListBase({
  position,
  isMerge,
  className,
  isHistory,
  selectedTokens,
  setSelectedTokens,
}: {
  selectedTokens?: string[];
  setSelectedTokens?: React.Dispatch<React.SetStateAction<string[]>>;
  position: ItemData;
  isMerge?: boolean;
  className?: string;
  isHistory?: boolean;
}) {
  const [isOpen, { open, close }] = useToggle();
  const isDesktop = useMediaQuery(screenGte.large);
  const history = useHistory();
  const status = position.status;

  const chainAssetPrice = useChainInfo(position.chain)?.price;
  const buyTxs = position?.transactions?.filter(
    (tx) =>
      tx.side === LimitOrderSide.Buy &&
      tx.status !== DexOrderStatus.confirmedCancelled
  );

  const failedDCA = position?.limitOrders?.filter((limit) =>
    buyTxs.find(
      (buyTx) =>
        buyTx.id === limit.transactionId &&
        orderStatesStatuses.error.includes(buyTx.status)
    )
  );

  const sellTxs = position?.transactions?.filter(
    (tx) => tx.side === LimitOrderSide.Sell
  );

  const failedTps = position?.takeProfits?.filter((tp) =>
    sellTxs.find(
      (x) =>
        x.id === tp.transactionId &&
        orderStatesStatuses.error.includes(x.status)
    )
  );

  const failedStopLoss = sellTxs?.filter(
    (x) =>
      x.id === position.trailingStopLoss?.transactionId &&
      orderStatesStatuses.error.includes(x.status)
  );

  const failedTpsNumber = failedTps ? failedTps.length : 0;
  const failedDCANumber = failedDCA ? failedDCA.length : 0;
  const failedStopLossNumber = failedStopLoss ? failedStopLoss.length : 0;
  const somethingFailed =
    positionStatesStatuses.active.includes(status) &&
    failedDCANumber + failedTpsNumber + failedStopLossNumber > 0;

  const tooltipText = somethingFailed && (
    <div className="xxs:space-y-0.5">
      <div>Orders with errors</div>
      <ul className="xxs:mx-0">
        {failedDCANumber > 0 ? (
          <li className="xxs:mx-0">DCA: {failedDCANumber} failed</li>
        ) : (
          ''
        )}
        {failedTpsNumber > 0 ? (
          <li className="xxs:mx-0">TP: {failedTpsNumber} failed</li>
        ) : (
          ''
        )}
        {failedStopLossNumber > 0 ? (
          <li className="xxs:mx-0">SL failed</li>
        ) : (
          ''
        )}
      </ul>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (isDesktop) {
            open();
          } else {
            history.push({
              pathname: `/dex/trade-monitoring/${
                isHistory ? Tabs.History : Tabs.OpenTrades
              }/${position.id}`,
              state: {
                from: `${history.location.pathname}${history.location.search}`,
              },
            });
          }
        }}
        className={twMerge(
          'text-left m-0 xxs:bg-dex-black-900 hover:bg-dex-black-800 xxs:p-2 w-full dark:text-dex-white xxs:text-xs flex xxs:gap-2 items-center',
          className
        )}
      >
        <div className="grow truncate">
          <div className="flex space-x-2 items-center">
            {SnipePositionStatus[position.status]?.canSell &&
              !isMerge &&
              !isDesktop &&
              selectedTokens &&
              setSelectedTokens && (
                <div>
                  <Checkbox
                    className="z-auto xxs:ml-1"
                    checked={selectedTokens.includes(position.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setSelectedTokens(
                        (prev) =>
                          e.target.checked
                            ? [...prev, position.id] // Add address
                            : prev.filter((t) => t !== position.id) // Remove address
                      );
                    }}
                  />
                </div>
              )}
            {position && (
              <TokenIconWithChain
                hasIcon={position.hasIcon}
                className="h-[25px] w-[25px]"
                tokenName={position.tokenName}
                address={position.tokenAddress}
                symbol={position.tokenSymbol}
                protocol={position.protocol?.protocol}
                chain={position.chain}
              />
            )}
            <div className="">
              <TokenNameWithChain
                className="mb-0"
                baseAsset={position.tokenSymbol}
                chain={position.chain}
              />
              <div className="flex text-xs space-x-0.5 items-center xxs:text-dex-white-secondary">
                <ClockIcon className="w-3 h-3" />
                <div className="">
                  {position.holdingTimeSeconds && !isHistory ? (
                    <HoldingTimeCounter start={position.holdingTimeSeconds} />
                  ) : position.holdingTimeSeconds ? (
                    formatHoldingTime(position)
                  ) : position.createdAt ? (
                    <DurationCounter start={position.createdAt} />
                  ) : (
                    '--'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="shrink-0">
          <div className="flex flex-col items-end">
            {isPositionExecuted(position.status) ? (
              <div className="flex space-x-1 items-center">
                {somethingFailed && (
                  <Tooltip text={tooltipText}>
                    <ExclamationTriangleIcon className="text-red-500 w-4 h-4" />
                  </Tooltip>
                )}
                <PnlComponent
                  position={position}
                  chainAssetPrice={chainAssetPrice}
                />
              </div>
            ) : (
              <SnipeStatus condensed position={position} />
            )}
          </div>
        </div>
        {status !== DexTradeStatus.closedInProgress && !isMerge && (
          <ActionsMenu position={position} />
        )}
      </button>
      {isOpen && (
        <FullScreenModal close={close}>
          <PositionDetails id={position.id} />
        </FullScreenModal>
      )}
    </>
  );
}
